import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState
} from 'react'

import { isEqual } from 'lodash'
import moment from 'moment'

import {
  formatSearchString,
  getAccountType,
  getRetailMediaCounterpartID,
  getWhiteLabelPublisherId,
  history,
  isAdvertiser,
  isPublisher
} from '~/helpers'
import handleQueryParams from '~/helpers/handleQueryParams'
import { handleSortDirection } from '~/helpers/sortData'
import { useAppInfo } from '~/hooks/useAppInfo'
import useAppSearchParams from '~/hooks/useAppSearchParams'
import useFetchSWR from '~/hooks/useFetchSWR'
import useQueryParams from '~/hooks/useQuery'
import { parseLookbackMonths } from '~/modules/retailMedia/constants'
import { formatListCampaign } from '~/modules/retailMedia/dtos/common/campaign/list'
import { getNtbOrdersWindow } from '~/modules/retailMedia/helpers/newMetricsAvailability'
import { useClickMetricsCutoffAlert } from '~/modules/retailMedia/hooks/useClickMetricsCutoffAlert'
import { useNtbAvailability } from '~/modules/retailMedia/hooks/useNtbAvailability'
import { reatailmediaApi } from '~/modules/retailMedia/services/api'
import { useAppSelector } from '~/store/hooks'

import { campaignListqueryKeyParams } from '../filter/keys'
import { schema } from '../schemas'

const CampaignTableContext = createContext<CampaignTableContextData>(
  {} as CampaignTableContextData
)

const dynamicKeys: (keyof GetCampaignsQueryParam)[] = [
  'publisher_id',
  'advertiser_id',
  'status',
  'name',
  'start_date',
  'end_date',
  'ad_type',
  'targeting_type',
  'tag_id',
  'lookback_months'
]

const CampaignTableProvider = ({ children }: React.PropsWithChildren) => {
  const { isWhiteLabel } = useAppInfo()

  const accountType = getAccountType() as 'grocery' | 'industry'

  const [data, setData] = useState(null)

  const searchParams = useQueryParams()

  const {
    rmid: paramId,
    tag_id,
    campaign_name: name,
    campaign_status: status,
    ad_type,
    campaign_targeting: targeting_type,
    order_direction,
    order_by,
    quantity,
    page,
    lookback_months
  } = useAppSearchParams<CampaignListSearchParams>(campaignListqueryKeyParams)

  const id = useMemo(
    () =>
      isWhiteLabel && isAdvertiser() ? getWhiteLabelPublisherId() : paramId,
    [isWhiteLabel, paramId]
  )

  const isListAll = !id && (!isWhiteLabel || (isWhiteLabel && isPublisher()))

  const [innerLoading, setInnerLoading] = useState(false)
  const [queryParams, setQueryParams] = useState({} as GetCampaignsQueryParam)

  /**
   * Redux
   */

  const { startDate, endDate } = useAppSelector(state => state.datepickerHeader)

  const params = useMemo(
    () =>
      ({
        [getRetailMediaCounterpartID()]: id,
        start_date: moment(startDate).format('YYYY-MM-DD'),
        end_date: moment(endDate).format('YYYY-MM-DD'),
        quantity,
        page,
        ad_type,
        status,
        targeting_type,
        name,
        account_info: isListAll,
        tag_id,
        order_direction,
        order_by,
        lookback_months
      }) as GetCampaignsQueryParam,
    [
      id,
      startDate,
      endDate,
      quantity,
      page,
      ad_type,
      status,
      targeting_type,
      name,
      isListAll,
      tag_id,
      order_direction,
      order_by,
      lookback_months
    ]
  )

  const urlGetCampaign = useMemo(
    () => `${reatailmediaApi}/campaign/v2?${formatSearchString(queryParams)}`,
    [queryParams]
  )

  const {
    data: dataSWR,
    error,
    isValidating,
    mutate
  } = useFetchSWR<ResponseListIndustryCampaign | ResponseListGroceryCampaign>({
    url: urlGetCampaign,
    refreshInterval: 60000,
    revalidateOnFocus: false,
    // queryParams is not empty
    shouldFetch: !!Object.keys(queryParams).length
  })

  useEffect(() => {
    if (dataSWR) {
      setData(dataSWR)
    }
  }, [accountType, dataSWR])

  const {
    data: campaigns,
    currentPage,
    pages,
    total
  }: CampaingDataMemo = useMemo(() => {
    setInnerLoading(false)
    return {
      currentPage: data?.currentPage,
      pages: data?.pages,
      total: data?.total,
      data: formatListCampaign(data?.data)
    }
  }, [data])

  const metricsSources = useMemo(
    () => campaigns?.map(item => item?.metrics?.raw || null) || [],
    [campaigns]
  )
  const availableMetrics = useNtbAvailability(metricsSources)

  const showClickAlert = useClickMetricsCutoffAlert()

  /**
   * HANDLE TOGGLE ACTIVE CAMPAIGN
   */

  const handleMutate = useCallback(
    async ({ value, data }: HandleMutateProps) => {
      const id = data.id
      const mutateData = {
        ...dataSWR,
        data: dataSWR.data.map(item =>
          item.id === id ? { ...item, active: value } : item
        )
      }

      await mutate(mutateData)
    },
    [dataSWR, mutate]
  )

  /**
   * Handle Pagination
   */
  const handlePagination = useCallback(
    (value: number, replace = false) => {
      searchParams.set('page', String(value))
      const nav = replace ? history.replace : history.push
      nav({ search: searchParams.toString() })
    },
    [searchParams]
  )

  const handleItemsPerPageChange = useCallback(
    (value: { value: number }) => {
      searchParams.set('quantity', String(value.value))
      searchParams.set('page', '1')
      history.push({ search: searchParams.toString() })
    },
    [searchParams]
  )

  const handleLookbackChange = useCallback(
    (value: number) => {
      searchParams.set('lookback_months', String(value))
      searchParams.set('page', '1')
      history.push({ search: searchParams.toString() })
    },
    [searchParams]
  )
  /**
   * Sort list
   */

  const handleSortList = useCallback(
    (data: OnSortProps) => {
      const selectedKey = data.key
      const sortInitialDirection = data?.sortInitialDirection

      const response = handleSortDirection({
        selectedKey,
        currentSortDirection: order_direction,
        currentSortKey: order_by,
        sortInitialDirection,
        allowReset: true
      })

      if (!response.sortDirection) {
        searchParams.delete('order_direction')
        searchParams.delete('order_by')
      }

      if (response.sortDirection) {
        searchParams.set('order_direction', response.sortDirection)
        searchParams.set('order_by', response.sortKey)
      }

      history.push({ search: searchParams.toString() })
    },
    [order_by, order_direction, searchParams]
  )

  /**
   * Handle coleteral effect
   */
  useLayoutEffect(() => {
    if (isEqual(params, queryParams)) {
      return
    }

    const { shouldResetFixedKeys } = handleQueryParams({
      params,
      currentParams: queryParams,
      dynamicKeys
    })

    if (shouldResetFixedKeys) {
      params.page = 1
      handlePagination(1, true)
    }

    setInnerLoading(true)
    setQueryParams(params)
  }, [handlePagination, params, queryParams])

  const contextValue = useMemo(
    () => ({
      data: campaigns,
      error,
      innerLoading,
      isValidating,
      currentPage,
      pages,
      page,
      perPage: quantity,
      total,
      schema: schema({
        isListAll,
        availableMetrics,
        showClickAlert,
        lookbackMonths: parseLookbackMonths(lookback_months),
        ntbOrdersWindow: getNtbOrdersWindow(endDate)
      }),
      handlePagination,
      handleItemsPerPageChange,
      handleMutate,
      sortDirection: order_direction,
      sortKey: order_by,
      handleSortList,
      queryParams: formatSearchString(queryParams),
      lookbackMonths: parseLookbackMonths(lookback_months),
      availableMetrics,
      handleLookbackChange
    }),
    [
      availableMetrics,
      showClickAlert,
      campaigns,
      currentPage,
      endDate,
      error,
      handleItemsPerPageChange,
      handleLookbackChange,
      handleMutate,
      handlePagination,
      handleSortList,
      innerLoading,
      isListAll,
      isValidating,
      lookback_months,
      order_by,
      order_direction,
      page,
      pages,
      quantity,
      queryParams,
      total
    ]
  )

  return (
    <CampaignTableContext.Provider value={contextValue}>
      {children}
    </CampaignTableContext.Provider>
  )
}

function useCampaignTable(): CampaignTableContextData {
  const context = useContext(CampaignTableContext)

  if (!context) {
    throw new Error(
      'useCampaignTable must be used within an CampaignTableProvider'
    )
  }

  return context
}

export { CampaignTableContext, CampaignTableProvider, useCampaignTable }
